import { getMsalUser } from "authentication";
import { execGetUserInfoQuery, GetUserInfoQuery } from "graphqlBase/userManagement/user/__generated__/getUserInfo";
import { useEffect } from "react";
import { atom, useRecoilValue, useSetRecoilState } from "recoil";
import wait from "utils/wait";
import { userInfoAtom } from "./state";
import { SimpleScope, UserInfo } from "./types";

/**
 * Atom to track whether the certificate form is in edit mode.
 * This atom is shared between standard and grid-forming certificate upload pages.
 * When a certificate is being edited (not replaced), this will contain the certificate ID.
 */
export const certificateToEditIdAtom = atom<string | undefined>({
  key: "certificateToEditIdAtom",
  default: undefined,
});

/**
 * Atom to track whether the certificate is being replaced.
 * Used to distinguish between edit mode (updating existing) and replace mode (creating new version).
 */
export const certificateToReplaceIdAtom = atom<string | undefined>({
  key: "certificateToReplaceIdAtom",
  default: undefined,
});

const noScopes = [] as SimpleScope[];

/**
 * Retrieves the user information with optional modifications.
 * @param args - Optional partial UserInfo object to modify the user information.
 * @returns The modified UserInfo object or undefined if the user is not available.
 */

export const useCreaetUserInfo = (isAuthenticated: boolean) => {
  const setUserInfo = useSetRecoilState(userInfoAtom);

  useEffect(() => {
    getUserWithScopes().then((res) => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      //@ts-ignore
      if (!res.tenantId) return;
      setUserInfo(res as UserInfo);
    });
  }, [setUserInfo, isAuthenticated]);
};

const getUserInfo = async (email: string): Promise<GetUserInfoQuery> => {
  let retryCount = 0;
  let data;

  while (retryCount < 3) {
    try {
      const res = await execGetUserInfoQuery({ variables: { email } });
      data = res.data;
      break;
    } catch (e) {
      await wait(1000);
      retryCount++;
    }
  }
  if (!data) throw new Error("Failed to fetch user info");
  return data;
};

export const getUserWithScopes = async () => {
  const user = getMsalUser();
  if (!user) return { scopes: noScopes };
  const email = user.idTokenClaims?.email as string; // Cast 'email' to string
  const data = await getUserInfo(email);
  if (!data) return { scopes: noScopes };
  const userInfo = data.users?.items?.[0];
  const role = userInfo?.tenant?.tenantRole;
  const approvalStatusOfTenant = userInfo?.tenant?.approvalStatus;
  const scopes =
    userInfo?.userBridgeRoles.flatMap<SimpleScope>(({ role }) =>
      role?.roleBridgeScopes.reduce<SimpleScope[]>((vals, { scope }) => {
        if (!scope) return vals;
        return [...vals, scope];
      }, [])
    ) ?? [];
  const tenantId = userInfo?.tenantId;
  const userRoleIds = userInfo?.userBridgeRoles.map(({ roleId }) => roleId) ?? [];
  if (!role || !tenantId) return { scopes: noScopes };
  return {
    ...user,
    tenantId,
    id: userInfo.id,
    firstName: userInfo.firstName,
    lastName: userInfo.lastName,
    displayName: userInfo.displayName,
    role: role,
    scopes,
    email,
    userRoleIds,
    approvalStatusOfTenant,
  };
};

export const useGetUserInfo = (): UserInfo | undefined => {
  const userInfo = useRecoilValue(userInfoAtom);
  return userInfo;
};

/**
 * Retrieves the roles of the current user.
 * @returns An array of strings representing the user roles.
 */
export const useGetUserRoles = (): string[] => {
  const user = useGetUserInfo();
  if (!user) return [];
  return user.idTokenClaims?.roles ?? [];
};

export const useGetUserScopes = (): string[] => {
  const user = useGetUserInfo();
  if (!user) return [];
  return user.scopes.map((scope) => scope.value);
};

/**
 * Hook to determine if the current user has permission to configure custom file visibility.
 * Uses the same permission logic as AccessPermissionManagement page access.
 *
 * @returns true if user can configure file visibility, false otherwise
 */
export const useHasFileVisibilityPermission = (): boolean => {
  const userInfo = useGetUserInfo();

  const userRolesWithAccessRequestManagementViewPermission = [
    "2e2f2352-7a98-404c-9cae-26a71989a872", // TenantAdmin
    "6e2f2352-7a98-404c-9cae-26a71989a872", // AccessRequestManager (Datenzugangsverwalter)
  ];

  // Tenant roles that can upload certificates
  const tenantRolesWithAccessRequestManagementViewPermission = [
    "MANUFACTURER",
    "CERTIFICATE_AUTHORITY",
    "PLATFORM_OPERATOR",
  ];

  const hasUserRole =
    userInfo?.userRoleIds?.some((roleId) => userRolesWithAccessRequestManagementViewPermission.includes(roleId)) ??
    false;

  const hasTenantRole = tenantRolesWithAccessRequestManagementViewPermission.includes(userInfo?.role ?? "");

  return hasUserRole && hasTenantRole;
};

/**
 * Hook to determine if the certificate form is currently in edit mode.
 * Edit mode is when updating an existing certificate, as opposed to:
 * - Creating a new certificate (upload)
 * - Replacing an existing certificate (new version)
 *
 * Custom visibility controls should be hidden in edit mode and only shown during upload.
 *
 * @returns true if in edit mode, false otherwise
 */
export const useIsCertificateEditMode = (): boolean => {
  const certificateToEditId = useRecoilValue(certificateToEditIdAtom);
  const certificateToReplaceId = useRecoilValue(certificateToReplaceIdAtom);

  // Edit mode: we have an edit ID and no replace ID
  return !!certificateToEditId && !certificateToReplaceId;
};
